import { ApiError } from '@/lib/errors'

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'
const TOKEN_KEY = 'kast_token'
const REFRESH_TOKEN_KEY = 'kast_refresh_token'

/**
 * Absolute URL for an OAuth start/connect endpoint on the API origin.
 * Must NOT be a same-origin `/api/auth/*` path: in prod the SPA is served
 * from www.kast.video (Cloudflare Pages) while the API lives at
 * api.kast.video, so `/api/auth/twitch` hits the Pages SPA fallback
 * (index.html) instead of starting OAuth. Build from VITE_API_URL instead.
 */
export function apiAuthUrl(provider: string): string {
  return `${BASE_URL}/auth/${provider}`
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
}

let refreshPromise: Promise<string> | null = null

async function refreshToken(): Promise<string> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const rt = getRefreshToken()
      if (!rt) throw new Error('refresh_failed')
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      })
      if (!res.ok) throw new Error('refresh_failed')
      const data = (await res.json()) as { token: string; refresh_token: string }
      setToken(data.token)
      setRefreshToken(data.refresh_token)
      return data.token
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth, ...fetchOptions } = options
  const headers = new Headers(fetchOptions.headers)

  if (!skipAuth) {
    const token = getToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
  }

  if (!headers.has('Content-Type') && fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
  })

  if (!response.ok) {
    if (response.status === 401 && !skipAuth && path !== '/auth/refresh') {
      try {
        const newToken = await refreshToken()
        // Retry the original request with the new token
        const retryHeaders = new Headers(fetchOptions.headers)
        retryHeaders.set('Authorization', `Bearer ${newToken}`)
        if (!retryHeaders.has('Content-Type') && fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
          retryHeaders.set('Content-Type', 'application/json')
        }
        const retryResponse = await fetch(`${BASE_URL}${path}`, {
          ...fetchOptions,
          headers: retryHeaders,
        })
        if (!retryResponse.ok) {
          if (retryResponse.status === 401) {
            clearToken()
            window.location.href = '/login?error=session_expired'
          }
          throw await ApiError.fromResponse(retryResponse)
        }
        if (retryResponse.status === 204 || retryResponse.headers.get('content-length') === '0') {
          return undefined as T
        }
        return retryResponse.json() as Promise<T>
      } catch (err) {
        if (err instanceof Error && err.message !== 'refresh_failed') throw err
        clearToken()
        window.location.href = '/login?error=session_expired'
      }
    }
    throw await ApiError.fromResponse(response)
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  return response.json() as Promise<T>
}

/**
 * Start an OAuth connect/login flow for a platform.
 *
 * Twitch `/auth/twitch` is the PUBLIC login route (no user yet) → navigate the
 * browser straight there. YouTube/Kick `/auth/{provider}` START routes are
 * auth-protected (they bind the authorize URL to the logged-in user via a
 * signed state/PKCE value) and return `{ url }` — so we must fetch them WITH
 * the bearer token, then redirect the browser to the returned URL. A bare
 * top-level `<a href>` to those would 401 (no Authorization header).
 */
export async function startPlatformConnect(
  provider: 'twitch' | 'youtube' | 'kick',
): Promise<void> {
  if (provider === 'twitch') {
    window.location.href = apiAuthUrl('twitch')
    return
  }
  const { url } = await api.get<{ url: string }>(`/auth/${provider}`)
  window.location.href = url
}

// ── Multi-platform chat sessions ──────────────────────────────────────────

/** Supported chat platforms accepted by `POST /sessions`. */
export type ChatPlatform = 'twitch' | 'youtube' | 'kick' | 'tiktok'

/** One channel to ingest, e.g. `{ platform: 'twitch', channel: 'foo' }`. */
export interface ChannelSpec {
  platform: ChatPlatform
  channel: string
}

/** Response from `POST /sessions` (mirrors `StartSessionResponse` on the chat service). */
export interface CreateSessionResponse {
  session_id: string
  /** Platforms that were actually started (TikTok is skipped when disabled server-side). */
  platforms: string[]
  /** Human-readable notes about channels that were skipped. */
  skipped?: string[]
}

/**
 * Start a multi-platform chat ingest session via `POST /sessions`.
 *
 * This is a protected route — the JWT `Authorization: Bearer` header the rest
 * of the app uses is attached automatically by `request()`. The returned
 * `session_id` is what the merged-feed WebSocket connects to (see `chatWsUrl`).
 */
export function createSession(
  channels: ChannelSpec[],
  sessionId?: string,
): Promise<CreateSessionResponse> {
  return api.post<CreateSessionResponse>('/chat/sessions', {
    ...(sessionId ? { session_id: sessionId } : {}),
    channels,
  })
}

/**
 * Build the WebSocket URL for a session's merged chat feed.
 *
 * The chat service serves the upgrade at `GET /ws/{session_id}`. Auth is
 * passed via the `Sec-WebSocket-Protocol` header as `bearer.{jwt}` to keep
 * the token out of server/CDN access logs.
 */
export function chatWsUrl(sessionId: string): string {
  const wsBase = wsBaseUrl()
  return `${wsBase}/chat/ws/${encodeURIComponent(sessionId)}`
}

/**
 * Build the WebSocket protocol list for authenticated connections.
 * Pass this as the second argument to `new WebSocket(url, protocols)`.
 */
export function chatWsProtocols(): string[] {
  const tok = getToken()
  return tok ? [`bearer.${tok}`, 'bearer'] : []
}

/** Resolve the WebSocket origin, deriving it from the API URL when needed. */
export function wsBaseUrl(): string {
  const explicit = import.meta.env.VITE_WS_URL
  if (explicit) return explicit.replace(/\/$/, '')
  // Derive from the API URL: https://api.kast.video -> wss://api.kast.video
  const apiBase = import.meta.env.VITE_API_URL
  if (apiBase) {
    return apiBase.replace(/^http/, 'ws').replace(/\/$/, '')
  }
  // Dev-only fallback — VITE_WS_URL or VITE_API_URL must be set in production
  if (import.meta.env.DEV) {
    return 'ws://localhost:3002'
  }
  throw new Error('VITE_WS_URL or VITE_API_URL must be set in production')
}

// ── Email auth ───────────────────────────────────────────────────────────────

export interface EmailLoginResponse {
  token: string
  refresh_token: string
}

/** Returned by POST /auth/login when the account has 2FA enabled. */
export interface LoginNeeds2faResponse {
  needs_2fa: true
  pending_token: string
}

export type LoginResponse = EmailLoginResponse | LoginNeeds2faResponse

export interface EmailRegisterResponse {
  token: string
  refresh_token: string
}

export function emailLogin(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', {
    skipAuth: true,
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

export interface TwoFaValidateResponse {
  token: string
  refresh_token: string
  user_id: string
  display_name: string
}

export function validate2fa(
  pending_token: string,
  code: string,
): Promise<TwoFaValidateResponse> {
  return request<TwoFaValidateResponse>('/auth/2fa/validate', {
    skipAuth: true,
    method: 'POST',
    body: JSON.stringify({ pending_token, code }),
  })
}

export interface TwoFaSetupResponse {
  otpauth_uri: string
  secret: string
}

export interface TwoFaVerifyResponse {
  enabled: boolean
  recovery_codes: string[]
}

export interface TwoFaStatusResponse {
  enabled: boolean
  recovery_codes_remaining: number
}

export function get2faStatus(): Promise<TwoFaStatusResponse> {
  return api.get<TwoFaStatusResponse>('/auth/2fa/status')
}

export function setup2fa(): Promise<TwoFaSetupResponse> {
  return api.post<TwoFaSetupResponse>('/auth/2fa/setup')
}

export function verify2fa(code: string): Promise<TwoFaVerifyResponse> {
  return api.post<TwoFaVerifyResponse>('/auth/2fa/verify', { code })
}

export function disable2fa(password: string): Promise<{ disabled: boolean }> {
  return api.post<{ disabled: boolean }>('/auth/2fa/disable', { password })
}

export function emailRegister(
  email: string,
  password: string,
  display_name: string,
): Promise<EmailRegisterResponse> {
  return request<EmailRegisterResponse>('/auth/register', {
    skipAuth: true,
    method: 'POST',
    body: JSON.stringify({ email, password, display_name }),
  })
}

export const api = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>(path, { ...options, method: 'GET' })
  },
  /**
   * Raw GET — returns the bare `Response` so callers can read blobs, text, or
   * inspect headers directly (e.g. for file downloads).  Auth header is
   * attached the same way as every other request; token refresh is NOT
   * attempted (callers that need it should use `get<T>` instead).
   */
  async getRaw(path: string, options?: RequestOptions): Promise<Response> {
    const { skipAuth, ...fetchOptions } = options ?? {}
    const headers = new Headers(fetchOptions.headers)
    if (!skipAuth) {
      const token = getToken()
      if (token) headers.set('Authorization', `Bearer ${token}`)
    }
    return fetch(`${BASE_URL}${path}`, { ...fetchOptions, headers })
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>(path, { ...options, method: 'DELETE' })
  },
  upload<T>(path: string, body: FormData, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'POST',
      body: body as unknown as BodyInit,
      headers: { ...(options?.headers as Record<string, string>) },
    })
  },
}
